import {
	Button,
	Colors,
	DialogContainer,
	Modal,
	ModalFooter,
	ModalHeading,
	ModalInputBox,
	ModalTitle,
	ModalTitleContainer,
	TextFieldWrapper,
} from "@factorearth/component-library";
import FormattedActionBar from "components/FormattedActionBar/FormattedActionBar";
import ProjectList from "components/ProjectList/ProjectList";
import { FieldTemplate, FormTemplate, FormTemplateForResolver, Option, ProjectForResolver, User } from "db/definitions";
import useClickOutside from "hooks/useClickOutside";
import { useTheme } from "hooks/useSingleton";
import { useTemplateManager } from "hooks/useTemplateManager";
import React, { useEffect, useState } from "react";
import { FiX } from "react-icons/fi";
import dropdownData from "utils/dropdownData.json";
import enforceContinuationData from "utils/enforceContinuationData";
import enforceFormTemplateDoc from "utils/enforceFormTemplateDoc";
import getContinuationData from "utils/getContinuationData";
import getFullFormTemplate from "utils/getFullFormTemplate";
import handleSearchTemplates from "utils/handleSearchTemplates";
import { v4 as uuid } from "uuid";
import DrawerTutorialButton from "../DrawerTutorial/DrawerTutorialButton/DrawerTutorialButton";
import FormTemplateList from "./FormTemplateList/FormTemplateList";
import styles from "./TemplateManager.module.scss";
import TemplateSearch from "./TemplateSearch/TemplateSearch";
import { parseTemplate } from "services/templates/parseXmlToJson";
import { recordSentry } from "utils/recordSentry";
import EditFormTemplate from "./EditFormTemplate/EditFormTemplate";
import { useErrorBoundary } from "react-error-boundary";
import LoadingSpinner from "components/LoadingSpinner/LoadingSpinner";
import { RecentProject } from "utils/localstorage/projects";
import { Location } from "hooks/useDrawer";

interface Props {
	addFormTemplateToListInState: (newTemplate: FormTemplateForResolver) => void;
	formTemplates: FormTemplateForResolver[];
	getProjects: () => Promise<void>;
	online: boolean;
	projectId: string;
	projects: ProjectForResolver[];
	recentProjects: RecentProject[];
	removeTemplateFromListInState: (deletedTemplateId: string) => void;
	setProjectId: React.Dispatch<React.SetStateAction<string>>;
	setupDrawer: (location: "" | Location) => void;
	userDoc: User;
}

function TemplateManager(props: Props) {
	const {
		addFormTemplateToListInState,
		formTemplates,
		getProjects,
		online,
		projectId,
		projects,
		recentProjects,
		removeTemplateFromListInState,
		setProjectId,
		setupDrawer,
		userDoc,
	} = props;

	const { deleteFormTemplate, setTemplateToEdit, templates, templateToEdit, uploadFormTemplates } = useTemplateManager();

	const [canEditTemplates, setCanEditTemplates] = useState(false);
	const [loaded, setLoaded] = useState(false);
	const [search, setSearch] = useState("");
	const { showBoundary } = useErrorBoundary();

	const color = useTheme();
	const { setOpenDropdownId } = useClickOutside(dropdownData, userDoc);

	useEffect(() => {
		const editTemplatesPerm = userDoc.roles.some((role) => role.permissions.some((perm) => perm.value === "Edit_Templates"));
		setCanEditTemplates(editTemplatesPerm);
		if (!editTemplatesPerm && online) {
			showBoundary(new Error("User does not have the required roles to access Template Manager"));
		}
	}, [userDoc]);

	useEffect(() => {
		if (formTemplates.length > 0) {
			setLoaded(true);
		} else {
			setLoaded(false);
		}
	}, [formTemplates]);

	async function handleDeleteTemplate(id: string) {
		await deleteFormTemplate(id);
		removeTemplateFromListInState(id);
		return true;
	}

	/**
	 * Setup the list of templates for users to edit.
	 * @returns HTML for the template list.
	 */
	function renderTemplateList() {
		const filteredTemplates = handleSearchTemplates(templates, search);
		const final = (
			<FormTemplateList
				color={color}
				handleDeleteTemplate={handleDeleteTemplate}
				handleEditTemplate={handleEditTemplate}
				online={online}
				templates={filteredTemplates}
				userDoc={userDoc}
				setupNewVersionDerived={setupNewVersionDerived}
			/>
		);

		return final;
	}

	/**
	 * Setup the template that needs to be edited.
	 * this manages whether a new version, derived version or existing version is used.
	 * @param data containing id and newVersion boolean
	 */
	async function handleEditTemplate(data: { id: string }) {
		setLoaded(false);
		const theTemplateToEdit = await getFullFormTemplate(data.id, online);
		if (!theTemplateToEdit) {
			recordSentry({ error: `No template found to edit with id: ${data.id}` });
			return;
		}
		const enforcedTemplateToEdit = enforceFormTemplateDoc(theTemplateToEdit);
		enforcedTemplateToEdit.fieldTemplates = theTemplateToEdit.fieldTemplates;
		setTemplateToEdit(enforcedTemplateToEdit);
		setLoaded(true);
	}

	/**
	 * Setup a new template based on another and put in state for editing.
	 * @param id of the formTemplate that needs to be copied.
	 */
	async function setupNewVersionDerived(id: string, versionData: { versionName: string; publish: boolean }) {
		setLoaded(false);
		const templateToDerive = await getFullFormTemplate(id, online);
		if (templateToDerive) {
			const theTemplateToEdit = await createNewTemplateDocFromAnother(templateToDerive, versionData);
			if (theTemplateToEdit) setTemplateToEdit(theTemplateToEdit);
		}
		setLoaded(true);
	}

	/**
	 * Creates a copy of another template and all its child field templates, continuationData and options
	 * @param templateToDeriveFrom that needs to have a copy made from.
	 * @returns the new template copy.
	 */
	async function createNewTemplateDocFromAnother(templateToDeriveFrom: FormTemplate, versionData: { versionName: string; publish: boolean }) {
		if (!templateToDeriveFrom.fieldTemplates || !templateToDeriveFrom.fieldsOrder) return;
		const newTemplate = enforceFormTemplateDoc(templateToDeriveFrom);
		newTemplate.id = uuid();
		newTemplate.group = userDoc.group;
		newTemplate.readGroups = userDoc.group === "FactorEarth" ? ["PERMISSION_DefaultTemplates"] : [];
		newTemplate.versionName = versionData.versionName;
		newTemplate.publish = versionData.publish;
		newTemplate.createdBy = userDoc.username;
		const newVersionNumber = uuid() + "_" + Date.now();
		newTemplate.version = newVersionNumber;
		// @ts-ignore
		newTemplate.new = true;
		if (projectId) {
			newTemplate.projectId = projectId;
		}
		const newFieldTemplates = [];
		const newFieldsOrder = [];
		const fieldTemplateMap: Record<string, FieldTemplate> = {};
		for (let i = 0; i < templateToDeriveFrom.fieldTemplates.items.length; i++) {
			const fieldTemplateToDeriveFromCopy: FieldTemplate = { ...templateToDeriveFrom.fieldTemplates.items[i] };
			const originalId = fieldTemplateToDeriveFromCopy.id;
			const fieldTemplateToDeriveFrom: FieldTemplate = { ...fieldTemplateToDeriveFromCopy };
			const baseFieldTemplate = fieldTemplateToDeriveFromCopy.baseFieldTemplate || fieldTemplateToDeriveFromCopy.id;
			fieldTemplateToDeriveFrom.id = uuid();
			fieldTemplateToDeriveFrom.group = userDoc.group;
			fieldTemplateToDeriveFrom.readGroups = userDoc.group === "FactorEarth" ? ["PERMISSION_DefaultTemplates"] : [];
			fieldTemplateToDeriveFrom.formTemplate = newTemplate.id;
			fieldTemplateToDeriveFrom.baseFieldTemplate = baseFieldTemplate;
			if (fieldTemplateToDeriveFrom.options?.items && fieldTemplateToDeriveFrom.options.items.length > 0) {
				const optionsMap = setupOptionsMap(fieldTemplateToDeriveFrom.options.items);

				// need to loop through the optionsOrder on the fieldTemplateToDeriveFrom then build the options in order off that and the map
				if (fieldTemplateToDeriveFrom.optionsOrder && fieldTemplateToDeriveFrom.optionsOrder.length > 0) {
					const newOptions = [];
					const newOptionsOrder = [];
					for (let i = 0; i < fieldTemplateToDeriveFrom.optionsOrder.length; i++) {
						const optionId = fieldTemplateToDeriveFrom.optionsOrder[i];
						const optionData = optionsMap[optionId];
						// THIS WAS A SHALLOW COPY ISSUE BASICALLY CAUSING INCORRECT IDS TO EXIST ON DOC vs. OPTIONS ORDER.
						if (optionData) {
							const option = { ...optionData };
							option.id = uuid();
							option.fieldTemplateId = fieldTemplateToDeriveFrom.id;
							option.formTemplate = newTemplate.id;
							option.group = userDoc.group;
							option.readGroups = userDoc.group === "FactorEarth" ? ["PERMISSION_DefaultTemplates"] : [];
							newOptions.push(option);
							newOptionsOrder.push(option.id);
						}
					}
					fieldTemplateToDeriveFrom.optionsOrder = newOptionsOrder;
					fieldTemplateToDeriveFrom.options.items = newOptions;
				}
			}

			if (
				fieldTemplateToDeriveFrom.tableFields?.items &&
				fieldTemplateToDeriveFrom.tableFields.items.length > 0 &&
				fieldTemplateToDeriveFrom.rowOrder &&
				fieldTemplateToDeriveFromCopy.tableFields
			) {
				let newRowOrder = [];
				let newTableFields = [];

				for (let ii = 0; ii < fieldTemplateToDeriveFrom.rowOrder.length; ii++) {
					const templateId = fieldTemplateToDeriveFrom.rowOrder[ii];
					// going for n^2 here, but this is a small list so should be ok
					const newTableField = fieldTemplateToDeriveFromCopy.tableFields.items.find((doc) => doc.id === templateId);

					if (!newTableField) {
						recordSentry({ error: `No matching table field found for templateId: ${templateId}` });
						continue;
					}

					const baseFieldTemplate = newTableField.baseFieldTemplate || newTableField.id;
					newTableField.id = uuid();
					newTableField.tableField = fieldTemplateToDeriveFrom.id;
					newTableField.version = newVersionNumber;
					newTableField.group = userDoc.group;
					newTableField.readGroups = userDoc.group === "FactorEarth" ? ["PERMISSION_DefaultTemplates"] : [];
					newTableField.baseFieldTemplate = baseFieldTemplate;
					newTableField.formTemplate = newTemplate.id;

					if (newTableField.options) {
						const optionsMap = setupOptionsMap(newTableField.options.items);

						// need to loop through the optionsOrder on the newTableField then build the options in order off that and the map
						if (newTableField.optionsOrder && newTableField.optionsOrder.length > 0) {
							let newOptions = [];
							const newOptionsOrder = [];
							for (let i = 0; i < newTableField.optionsOrder.length; i++) {
								const optionId = newTableField.optionsOrder[i];
								const optionData = optionsMap[optionId];
								// THIS WAS A SHALLOW COPY ISSUE BASICALLY CAUSING INCORRECT IDS TO EXIST ON DOC vs. OPTIONS ORDER.
								if (optionData) {
									const option = { ...optionData };
									option.id = uuid();
									option.fieldTemplateId = newTableField.id;
									option.formTemplate = newTemplate.id;
									option.group = userDoc.group;
									option.readGroups = userDoc.group === "FactorEarth" ? ["PERMISSION_DefaultTemplates"] : [];
									newOptions.push(option);
									newOptionsOrder.push(option.id);
								}
							}
							newTableField.optionsOrder = newOptionsOrder;
							newTableField.options.items = newOptions;
						}
					}
					newRowOrder.push(newTableField.id);
					newTableFields.push(newTableField);
				}

				fieldTemplateToDeriveFrom.tableFields.items = newTableFields;
				fieldTemplateToDeriveFrom.rowOrder = newRowOrder;
			}

			if (fieldTemplateToDeriveFrom.continuationDataId) {
				const continuationData =
					fieldTemplateToDeriveFrom.continuationData || (await getContinuationData(fieldTemplateToDeriveFrom.continuationDataId, online));
				if (continuationData) {
					continuationData.readGroups = userDoc.group === "FactorEarth" ? ["PERMISSION_DefaultTemplates"] : [];
					continuationData.group = userDoc.group;
					const enforced = enforceContinuationData(continuationData);
					enforced.id = uuid();
					enforced.fieldTemplateId = fieldTemplateToDeriveFrom.id;
					enforced.formTemplateId = newTemplate.id;
					fieldTemplateToDeriveFrom.continuationDataId = enforced.id;
					fieldTemplateToDeriveFrom.continuationData = enforced;
				}
			}

			fieldTemplateMap[originalId] = fieldTemplateToDeriveFrom;
		}

		for (let i = 0; i < templateToDeriveFrom.fieldsOrder.length; i++) {
			const orderedId = templateToDeriveFrom.fieldsOrder[i];
			const fieldTemplateToAdd = fieldTemplateMap[orderedId];
			newFieldsOrder.push(fieldTemplateToAdd.id);
			newFieldTemplates.push(fieldTemplateToAdd);
		}

		newTemplate.fieldsOrder = newFieldsOrder;
		newTemplate.fieldTemplates = { items: newFieldTemplates };
		return newTemplate;
	}

	function setupOptionsMap(options: Option[]) {
		const map: Record<string, Option> = {};
		for (let i = 0; i < options.length; i++) {
			const option = options[i];
			let id = option.id;
			map[id] = option;
		}
		return map;
	}

	function handleNewFormTemplate({ template }: { template: string }) {
		setLoaded(false);
		const newTemplate = enforceFormTemplateDoc({
			id: uuid(),
			group: userDoc.group,
			readGroups: userDoc.group === "FactorEarth" ? ["PERMISSION_DefaultTemplates"] : [],
			template,
			templateName: template,
		});
		if (projectId) {
			newTemplate.projectId = projectId;
		}
		newTemplate.fieldTemplates = { items: [] };
		// @ts-ignore
		newTemplate.new = true;
		newTemplate.publish = false;
		newTemplate.version = uuid() + "_" + Date.now();
		newTemplate.versionName = "default";
		newTemplate.type = "default";
		newTemplate.createdBy = userDoc.username;
		setTemplateToEdit(newTemplate);
		setLoaded(true);
	}

	function handleChangeProject(e: { target: { value: string } }) {
		setLoaded(false);
		const { value } = e.target;
		setProjectId(value);
		if (setOpenDropdownId) setOpenDropdownId(null);
		setLoaded(true);
	}

	async function handleUploadFormTemplates(e: React.ChangeEvent<HTMLInputElement>) {
		if (!e.target.files) return;
		const file = e.target.files[0];
		e.target.value = "";
		setLoaded(false);
		try {
			await uploadFormTemplates(file);
		} catch (err) {
			recordSentry({ error: err, msg: "Error uploading template JSON:", showModal: true });
		} finally {
			setLoaded(true);
		}
	}

	async function handleParseDotx(e: React.ChangeEvent<HTMLInputElement>) {
		if (!e.target.files) return;
		const file = e.target.files[0];
		e.target.value = "";
		const result = await parseTemplate(file);
		if (!result) return;
		if (result.error) {
			console.error("Template parse error:", result.error);
			return;
		}
		if (result.warnings.length > 0) {
			console.warn("Template parse warnings:", result.warnings);
		}
		if (result.templateJson) {
			const jsonString = JSON.stringify(result.templateJson, null, 2);
			const jsonFileName = file.name.replace(/\.dotx$/i, ".json");
			const jsonFile = new File([jsonString], jsonFileName, { type: "application/json" });

			setLoaded(false);
			try {
				await uploadFormTemplates(jsonFile);
			} catch (err) {
				recordSentry({ error: err, msg: "Error uploading parsed template:", showModal: true });
			} finally {
				setLoaded(true);
			}

			const url = URL.createObjectURL(jsonFile);
			const a = document.createElement("a");
			a.href = url;
			a.download = jsonFileName;
			a.click();
			URL.revokeObjectURL(url);
		}
	}

	/**
	 * close edit template handler
	 */
	function closeEditTemplate() {
		setTemplateToEdit(null);
	}
	if (!loaded) return <LoadingSpinner />;

	return (
		canEditTemplates && (
			<div style={{ background: color.background.primary }} className={styles.templateManagerWrapper}>
				{!templateToEdit && (
					<div className={styles.templateManager_landing}>
						<div className={styles.template_project_list}>
							<div style={{ color: color.text.primary }} className={styles.project_list_label}>
								Current Project:
							</div>
							<ProjectList
								color={color}
								projects={projects}
								recentProjects={recentProjects}
								online={online}
								getProjects={getProjects}
								handleChangeProject={handleChangeProject}
								projectId={projectId}
							/>
						</div>
						<FormattedActionBar color={color}>
							<CreateNewTemplateButton color={color} handleNewFormTemplate={handleNewFormTemplate} />
							<>
								{userDoc?.isDeveloper && (
									<>
										<button className={styles.new_template_btn}>
											<label className={styles.file_upload_label} htmlFor="TEMPLATE_UPLOAD">
												Upload JSON
												<input
													id="TEMPLATE_UPLOAD"
													type="file"
													style={{ display: "none" }}
													accept="application/json"
													onChange={handleUploadFormTemplates}
													className={styles.file_input}
												/>
											</label>
										</button>
										<button className={styles.new_template_btn}>
											<label className={styles.file_upload_label} htmlFor="DOTX_UPLOAD">
												Parse .dotx
												<input
													id="DOTX_UPLOAD"
													type="file"
													style={{ display: "none" }}
													accept=".dotx"
													onChange={handleParseDotx}
													className={styles.file_input}
												/>
											</label>
										</button>
									</>
								)}
							</>
							<TemplateSearch setSearch={setSearch} search={search} color={color} />
							<div style={{ background: color.background.primary }}>
								<DrawerTutorialButton
									helpColor={color.buttonText.outlined}
									location={"template-manager"}
									setupDrawer={setupDrawer}
									style={{ border: "0px" }}
								/>
							</div>
						</FormattedActionBar>
					</div>
				)}
				{templateToEdit ? (
					<EditFormTemplate
						addFormTemplateToListInState={addFormTemplateToListInState}
						closeEditTemplate={closeEditTemplate}
						online={online}
						setupDrawer={setupDrawer}
						userDoc={userDoc}
					/>
				) : (
					<div
						style={{
							border: `1px solid ${color.border.primary}`,
							background: color.background.primary,
						}}
						className={styles.edit_wrapper}>
						<div
							style={{
								color: color.text.primary,
							}}
							className={styles.listHead}>
							<div className={styles.nameHeader}>Template Groups</div>
						</div>
						<div>{renderTemplateList()}</div>
					</div>
				)}
			</div>
		)
	);
}

interface CreateNewTemplateButtonProps {
	color: Colors;
	handleNewFormTemplate: ({ template }: { template: string }) => void;
}

function CreateNewTemplateButton(props: CreateNewTemplateButtonProps) {
	const { color, handleNewFormTemplate } = props;

	const [open, setOpen] = useState(false);
	const [input, setInput] = useState("");

	function submit() {
		if (input) {
			setOpen(false);
			handleNewFormTemplate({ template: input });
		}
	}

	return (
		<>
			<Button colorPalette={color} className={styles.project_button} onClick={() => setOpen(true)} variant="filled">
				<div data-test="tm-new-button">+ New Template</div>
			</Button>
			<Modal open={open} colorPalette={color} setOpen={setOpen}>
				<DialogContainer>
					<ModalTitleContainer>
						<ModalTitle>New FormTemplate</ModalTitle>
						<FiX onClick={() => setOpen(false)} size={24} />
					</ModalTitleContainer>
					<ModalInputBox>
						<ModalHeading>Enter a name for the template</ModalHeading>
						<TextFieldWrapper
							colorPalette={color}
							value={input}
							data-test="create-new-template-input"
							onChange={(e) => {
								setInput(e.target.value);
							}}
						/>
					</ModalInputBox>
					<ModalFooter>
						<Button colorPalette={color} variant="filled" label="Create" onClick={submit} data-test="create-new-template-button" />

						<Button colorPalette={color} variant="destructive" label="Cancel" onClick={() => setOpen(false)} />
					</ModalFooter>
				</DialogContainer>
			</Modal>
		</>
	);
}

export default TemplateManager;
